import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Form, Icon, Popover, Tooltip } from '@syneto/compass-react';
import { trimString } from '../../../helpers/string';
import { ADDONS_PANEL_LOADING, ADDONS_PANEL_STATUS } from '../buildAddonsPanel';
import { MAINTENANCE_PANEL_LOADING, MAINTENANCE_PANEL_STATUS } from '../buildMaintenancePanel';
import { VALIDITY_STATUS, formatCalendarDate, getMaintenanceExpiry, toServerCalendarDate } from '../../../helpers/ExpiryRule';
import { formatMaintenanceDuration, getCurrentMaintenanceTerm, isSoftwareOnlyTrial } from '../../../helpers/Maintenance';
import { AddonsSection } from './AddonsSection';
import { ValidityBadge } from './ValidityBadge';
import { parseOSVersion, isOSVersionAtLeast } from '../../../helpers/OSVersionParser';
import { NotificationContext } from '../../../contexts/NotificationContext';
import { Loading } from '../../../components/Loading';
import { StoreContext } from '../../../contexts/StoreContext';
import { ServiceContext } from '../../../contexts/ServiceContext';
import { SerenityEventsButton } from './SerenityEventsButton';
import { UpgradeChannelBadge } from './UpgradeChannelBadge';
import InfoIcon from '../../../components/Form/InfoIcon';

const MINIMUM_PREVIEW_UPDATES_OS_VERSION = '6.0.5';
const OPEN_A_TICKET_URL = 'https://helpdesk.syneto.eu/support/tickets/new';
const NOT_AVAILABLE = 'N/A';

const MAINTENANCE_FAILED_TEXT = 'Maintenance could not be loaded.';
const MAINTENANCE_UNAVAILABLE_TEXT = 'Maintenance details are not available.';
const NO_MAINTENANCE_TEXT = 'No maintenance.';
const MAINTENANCE_NOT_STARTED_LABEL = 'Not started';
const TRIAL_LABEL = 'Trial';

export const MachineDetails = (props) => {
	const {
		machine,
		generateMaintenanceCertificationPdf,
		partnerPreviewParticipating,
		partnerPreviewEnrollmentEnabled,
		partnerPreviewEnrollmentsRefreshCount,
		currentUpgradeChannel,
		canManagePartnerPreview,
		onTogglePreviewUpdates,
		addons = ADDONS_PANEL_LOADING,
		maintenance: maintenancePanel = MAINTENANCE_PANEL_LOADING,
		expiringInterval = null,
		addonsSectionRequest = null,
		onAddonsSectionRequestHandled,
	} = props;
	const { settingsService } = useContext(ServiceContext);
	const { pushSuccessfulNotification, pushDangerNotification } = useContext(NotificationContext);
	const { state } = useContext(StoreContext);
	const { loggedInUser } = state;
	// machine.user is null when the company users are not available to the caller, or when the
	// activating user is no longer in the company.
	const activatedByName = machine.user
		? `${machine.user.profile.firstName} ${machine.user.profile.lastName}`
		: null;

	const [serenityEventsSettings, setSerenityEventsSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [displayedPreviewUpdatesEnabled, setDisplayedPreviewUpdatesEnabled] = useState(
		Boolean(partnerPreviewEnrollmentEnabled)
	);
	// Scoped to the machine whose toggle is in flight, not a plain boolean: `<MachineDetails>`
	// is reconciled rather than remounted on a machine switch (no `key`), so a single shared
	// boolean would keep a newly selected, otherwise-eligible machine's switch disabled for the
	// remainder of a previous machine's in-flight request.
	const [submittingForMachineId, setSubmittingForMachineId] = useState(null);

	// A stale optimistic value must not leak across machine selections or survive a parent
	// refresh: whenever the selected machine changes, or the parent's own source of truth
	// changes (after a successful mutation), the displayed toggle re-syncs to it.
	// partnerPreviewEnrollmentsRefreshCount is included on its own: a completed refresh can
	// report the same enabled value the optimistic update already showed, in which case
	// partnerPreviewEnrollmentEnabled would not change, but the displayed value must still be
	// re-derived from the refreshed prop rather than left standing on the optimistic guess.
	useEffect(() => {
		setDisplayedPreviewUpdatesEnabled(Boolean(partnerPreviewEnrollmentEnabled));
	}, [machine.id, partnerPreviewEnrollmentEnabled, partnerPreviewEnrollmentsRefreshCount]);

	// Tracks which machine is currently selected so an in-flight toggle can tell, once it
	// settles, whether the user has since moved on to a different machine.
	const selectedMachineIdRef = useRef(machine.id);

	useEffect(() => {
		selectedMachineIdRef.current = machine.id;
	}, [machine.id]);

	useEffect(() => {
		loadMachineDetailsData();
	}, [machine]);

	const loadMachineDetailsData = async () => {
		try {
			setSerenityEventsSettings(await settingsService.getSerenityEventsSettings());
			setLoading(false);
		} catch (error) {
			pushDangerNotification('Failed to fetch machine details data.');
		}
	};

	const sendSerenityRequestEmail = async () => {
		try {
			await settingsService.sendSerenityRequestEmail(loggedInUser.id, machine.serialNumber);
			pushSuccessfulNotification('SerenITy request has been successfully sent.');
		} catch (error) {
			pushDangerNotification('Failed to send SerenITy request.');
		}
	};

	const isEligibleForPreviewUpdates = isOSVersionAtLeast(machine.osVersion, MINIMUM_PREVIEW_UPDATES_OS_VERSION);
	const canTogglePreviewUpdates = canManagePartnerPreview && isEligibleForPreviewUpdates;

	// Optimistic with revert: the switch flips immediately, the parent mutation runs, and only a
	// rejection rolls the displayed value back to the last value the parent actually persisted
	// (the `partnerPreviewEnrollmentEnabled` prop). The parent already pushes both notifications.
	// The toggled machine id and the revert value are both captured at click time, since by the
	// time the request settles the user may have selected a different machine: the resync effect
	// above already owns that machine's displayed value, so the revert must not overwrite it.
	const handleTogglePreviewUpdates = async () => {
		const nextPreviewUpdatesEnabled = !displayedPreviewUpdatesEnabled;
		const toggledMachineId = machine.id;
		const revertTo = Boolean(partnerPreviewEnrollmentEnabled);

		setDisplayedPreviewUpdatesEnabled(nextPreviewUpdatesEnabled);
		setSubmittingForMachineId(toggledMachineId);

		try {
			await onTogglePreviewUpdates(machine, nextPreviewUpdatesEnabled);
		} catch (error) {
			if (selectedMachineIdRef.current === toggledMachineId) {
				setDisplayedPreviewUpdatesEnabled(revertTo);
			}
		} finally {
			// A newer toggle (on this machine or another) may already be in flight by the time
			// this one settles; only clear the indicator if it still belongs to this request.
			setSubmittingForMachineId((currentSubmittingMachineId) => {
				return currentSubmittingMachineId === toggledMachineId ? null : currentSubmittingMachineId;
			});
		}
	};

	// The page decides what the Maintenance section shows (buildMaintenancePanel); the pane renders a ready panel's
	// maintenance (null for a machine without one) or one line for a failed or unavailable panel.
	const maintenanceKnown = maintenancePanel.status === MAINTENANCE_PANEL_STATUS.READY;
	const maintenance = maintenanceKnown ? maintenancePanel.maintenance : null;
	const currentMaintenanceTerm = getCurrentMaintenanceTerm(maintenance);
	const maintenancePlanName = currentMaintenanceTerm?.plan?.customerFacingName ?? NOT_AVAILABLE;
	const hasSerenity = Boolean(currentMaintenanceTerm?.hasSerenity);

	// The dates are UTC instants whose written day is the day to show (toServerCalendarDate), whatever the browser's
	// time zone. An unknown interval (the page's setting call has not resolved) makes the badge Expired or Active only,
	// never Expiring, so the pane agrees with the list, which shows no icon until the interval is known.
	const maintenanceStartDate = toServerCalendarDate(maintenance?.startDate);
	const maintenanceExpiry = getMaintenanceExpiry(maintenance?.expiresOn, expiringInterval);
	const maintenanceExpirationDate = maintenanceExpiry?.calendarDate ?? null;
	const maintenanceExpired = maintenanceExpiry?.status === VALIDITY_STATUS.EXPIRED;
	const maintenanceDuration = formatMaintenanceDuration(maintenance);

	// The page decides what the section shows (buildAddonsPanel); the pane only renders a ready or a failed panel.
	const showAddonsSection = addons.status === ADDONS_PANEL_STATUS.READY || addons.status === ADDONS_PANEL_STATUS.FAILED;

	const formatMaintenanceDate = (calendarDate) => {
		return calendarDate ? formatCalendarDate(calendarDate) : NOT_AVAILABLE;
	};

	// A maintenance without dates has not started; the status follows the end once there is one.
	const renderMaintenanceStatus = () => {
		if (!maintenanceExpirationDate) {
			return <Badge appearance="secondary">{MAINTENANCE_NOT_STARTED_LABEL}</Badge>;
		}

		return (
			<ValidityBadge
				status={maintenanceExpiry.status ?? VALIDITY_STATUS.ACTIVE}
				label={isSoftwareOnlyTrial(maintenance) ? TRIAL_LABEL : undefined}
			/>
		);
	};

	const manualDownloadUrl = `https://storage.googleapis.com/central-manuals/user-guide-syneto-os-${parseOSVersion(machine.osVersion)}.pdf`;

	function renderSerenityEnabled(serenityEnabled) {
		let status, message;

		status = 'Not supported';
		message = (
			<div className="m-1">
				<div className="pb-3">
					SerenITy is the pro-active support service created by Syneto for constant monitoring of systems and sending
					automatic alerts to Customer Support.
				</div>
				<div className="pb-3">
					This machine is not entitled for the SerenITy service. Our sales team can get in touch to inform you how it
					can be activated.
				</div>
				<div>
					If you want to learn how, click the [Send request] button below. You will be contacted at the following email
					address:
				</div>
				<div className="pb-3 font-weight-500">{loggedInUser.email}</div>
				<div className="container p-0">
					<div className="float-left pt-2 mb-3">
						<a
							className="btn-link"
							href={serenityEventsSettings.serenityMarketingSite}
							target="_blank"
							rel="noopener noreferrer"
						>
							Learn more <Icon name="fa fa-external-link" />
						</a>
					</div>
					<div className="float-right mb-3">
						<Button type="submit" onClick={sendSerenityRequestEmail}>
							Send request
						</Button>
					</div>
				</div>
			</div>
		);

		if (hasSerenity && serenityEnabled === true) {
			status = 'Active';
			message = (
				<div>
					<div className="pb-2">This machine is protected using a proactive support service called SerenITy.</div>
					<a
						className="btn-link"
						href={serenityEventsSettings.serenityMarketingSite}
						target="_blank"
						rel="noopener noreferrer"
					>
						Learn more <Icon name="fa fa-external-link" />
					</a>
				</div>
			);
		}

		if (hasSerenity && (serenityEnabled === false || serenityEnabled == null)) {
			status = 'Inactive';
			message = (
				<div className="m-1">
					<div className="pb-3">
						This machine is entitled to the proactive support service called SerenITy. However, SerenITy is currently
						disabled for this appliance.
					</div>
					<div className="container p-0">
						<div className="float-left pt-2 pb-3">
							<a
								className="btn-link"
								href={serenityEventsSettings.serenityMarketingSite}
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more <Icon name="fa fa-external-link" />
							</a>
						</div>
					</div>
				</div>
			);
		}

		if (maintenanceExpired) {
			status = 'Inactive';
			message = (
				<div className="m-1">
					<div className="pb-3">
						This machine is no longer protected using a proactive support service called SerenITy, as its maintenance
						has expired.
					</div>
					<div className="container p-0">
						<div className="float-left pt-2 pb-3">
							<a
								className="btn-link"
								href={serenityEventsSettings.serenityMarketingSite}
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more <Icon name="fa fa-external-link" />
							</a>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div style={{ cursor: 'help' }}>
				<Popover body={message} trigger="click">
					<Badge appearance={status === 'Active' ? 'success' : 'danger'}>{status}</Badge>
				</Popover>
			</div>
		);
	}

	return loading || maintenancePanel.status === MAINTENANCE_PANEL_STATUS.LOADING ? (
		<Loading />
	) : (
		<>
			<h5 className="mb-4">Machine Details</h5>
			<p className="text-muted">Device Details</p>
			<div className="d-flex justify-content-between">
				<p>Machine Name</p>
				{machine.name ? <p className="text-primary">{machine.name}</p> : 'N/A'}
			</div>
			{partnerPreviewParticipating && (
				<div className="d-flex justify-content-between">
					<p>Version Type</p>
					<UpgradeChannelBadge currentUpgradeChannel={currentUpgradeChannel} />
				</div>
			)}
			<div className="d-flex justify-content-between mb-4">
				<p>Activated by</p>
				{activatedByName === null ? (
					'N/A'
				) : activatedByName.length > 40 ? (
					<Tooltip placement="bottom" theme="light" text={activatedByName}>
						<Badge appearance="secondary">{trimString(activatedByName, 40)}</Badge>
					</Tooltip>
				) : (
					<Badge appearance="secondary">{activatedByName}</Badge>
				)}
			</div>
			<p className="text-muted">Maintenance</p>
			{maintenancePanel.status === MAINTENANCE_PANEL_STATUS.FAILED && <p className="mb-4">{MAINTENANCE_FAILED_TEXT}</p>}
			{maintenancePanel.status === MAINTENANCE_PANEL_STATUS.UNAVAILABLE && <p className="mb-4">{MAINTENANCE_UNAVAILABLE_TEXT}</p>}
			{maintenanceKnown && !maintenance && <p className="mb-4">{NO_MAINTENANCE_TEXT}</p>}
			{maintenance && (
				<>
					<div className="d-flex justify-content-between">
						<p>Status</p>
						{renderMaintenanceStatus()}
					</div>
					<div className="d-flex justify-content-between">
						<p>Type</p>
						<div>
							{maintenancePlanName}
							<Popover
								trigger="click"
								body={
									<>
										{currentMaintenanceTerm?.plan?.endUserDescription}
										<div className="pt-2">
											<a className="btn-link" href="https://syneto.eu/support/" target="_blank" rel="noopener noreferrer">
												Read more <Icon name="fa fa-external-link" />
											</a>
										</div>
									</>
								}
							>
								<span>
									<Icon name="fa fa-question-circle ms-2" />
								</span>
							</Popover>
							{maintenanceExpirationDate && (
								<Tooltip placement="left" theme="light" text="Download maintenance certification">
									<a
										className="text-neutral-90"
										style={{ cursor: 'pointer' }}
										onClick={() => {
											return generateMaintenanceCertificationPdf(
												formatCalendarDate(maintenanceExpirationDate),
												maintenancePlanName,
												machine.serialNumber
											);
										}
										}
									>
										<Icon name="fa fa-file-pdf ms-2" />
									</a>
								</Tooltip>
							)}
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p>Duration</p>
						<p>{maintenanceDuration ?? NOT_AVAILABLE}</p>
					</div>
					<div className="d-flex justify-content-between">
						<p>Start Date</p>
						<p>{formatMaintenanceDate(maintenanceStartDate)}</p>
					</div>
					<div className="d-flex justify-content-between mb-4">
						<p>Expiration Date</p>
						<p>{formatMaintenanceDate(maintenanceExpirationDate)}</p>
					</div>
				</>
			)}
			{showAddonsSection && (
				<AddonsSection
					status={addons.status}
					rows={addons.rows}
					request={addonsSectionRequest?.machineId === machine.id ? addonsSectionRequest : null}
					onHandled={onAddonsSectionRequestHandled}
				/>
			)}
			{partnerPreviewParticipating && (
				<>
					<p className="text-muted">Software Update</p>
					{/* align-items-center absorbs the label's bottom margin, which every other row
					    leaves as trailing space -- mb-3 restores it so the next section heading
					    sits at the same distance as the others. */}
					<div className="d-flex justify-content-between align-items-center mb-3">
						<p>Enable SynetoOS Preview Updates</p>
						<div className="d-flex align-items-center">
							{!isEligibleForPreviewUpdates && (
								<>
									<span className="text-muted small me-2">Minimum required version: 6.0.5</span>
									<InfoIcon
										placement="top"
										text="Preview Updates requires SynetoOS 6.0.5 or later on this machine."
										tooltipStyle="mt-0 me-2"
									/>
								</>
							)}
							{canManagePartnerPreview ? (
								<Form.Switch
									checked={displayedPreviewUpdatesEnabled}
									disabled={!canTogglePreviewUpdates || submittingForMachineId === machine.id}
									onChange={() => { return handleTogglePreviewUpdates(); }}
								/>
							) : (
								<Tooltip placement="left" theme="light" text="You need fleet management permission to change this setting.">
									<div className="cannotShowTooltipsOnDisabledButtons">
										<Form.Switch
											checked={displayedPreviewUpdatesEnabled}
											disabled={true}
											onChange={() => {}}
										/>
									</div>
								</Tooltip>
							)}
						</div>
					</div>
				</>
			)}
			<p className="text-muted">Support</p>
			{maintenanceKnown && (
				<div className="d-flex justify-content-between">
					<p>SerenITy Status</p>
					{renderSerenityEnabled(machine.serenityEnabled)}
				</div>
			)}
			<div className="d-flex justify-content-between">
				<p>Need help?</p>
				<a className="btn-link" href={OPEN_A_TICKET_URL} target="_blank" rel="noopener noreferrer">
					Open a ticket <Icon name="fa fa-external-link" />
				</a>
			</div>
			<div className="d-flex justify-content-between">
				<p>User guide</p>
				<a className="btn-link" href={manualDownloadUrl} target="_blank" rel="noopener noreferrer">
					Download manual <Icon name="fa fa-external-link" />
				</a>
			</div>
			{hasSerenity &&
				machine.serenityEnabled === true &&
				!maintenanceExpired && (
				<div className="d-flex justify-content-between">
					<p>SerenITy Events</p>
					<SerenityEventsButton machine={machine} />
				</div>
			)}
		</>
	);
};

MachineDetails.propTypes = {
	machine: PropTypes.object.isRequired,
	partnerPreviewParticipating: PropTypes.bool,
	partnerPreviewEnrollmentEnabled: PropTypes.bool,
	partnerPreviewEnrollmentsRefreshCount: PropTypes.number,
	currentUpgradeChannel: PropTypes.string,
	canManagePartnerPreview: PropTypes.bool,
	onTogglePreviewUpdates: PropTypes.func,
	addons: PropTypes.shape({ status: PropTypes.string, rows: PropTypes.array }),
	maintenance: PropTypes.shape({ status: PropTypes.string, maintenance: PropTypes.object }),
	expiringInterval: PropTypes.number,
	addonsSectionRequest: PropTypes.shape({ machineId: PropTypes.string }),
	onAddonsSectionRequestHandled: PropTypes.func,
};
